import React from 'react'
import styled, { css } from 'styled-components'
import { rem } from 'polished'
import { IconButton } from 'components/Button'
import Flex, { Text, FlexProps } from 'components/Box'
import GradientSeparator from 'components/GradientSeparator'
import { ReactComponent as Logo } from 'icons/logo.svg'
import { ReactComponent as LogoBlack } from 'icons/logo-black.svg'
import NavbarFilter from './NavbarFilter'
import { NavbarLinkWrapper } from './NavbarLinkWrapper'
import { truncateStyles } from 'components/Truncate'
import { breakpoint } from 'utils'

const NavbarButton = styled(IconButton).attrs(({ size }) => ({
  size: size || 18,
}))`
  position: relative;
  height: ${rem(48)};
  width: ${rem(48)};
  color: ${({ theme }) => theme.colors.gray};

  :hover {
    color: ${({ theme }) => theme.colors.woodsmoke};
  }

  /* Show the button as hovered when it's inside of a expanded dropdown */
  [aria-expanded='true'] & {
    color: ${({ theme }) => theme.colors.woodsmoke};
  }

  ${props =>
    props.disabled &&
    css`
      opacity: 0.4;
    `};

  ${props => {
    if (props.active) {
      return css`
        color: ${({ theme }) => theme.colors.primaryTint[100]};

        &:hover,
        &:focus {
          color: ${({ theme }) => theme.colors.primaryTint[100]};
        }

        &:active {
          color: ${({ theme }) => theme.colors.primaryTint[100]};
        }
      `
    }
  }}
`

const NavbarSection = styled(Flex)<
  FlexProps & { align?: 'start' | 'center' | 'end' }
>`
  display: flex;
  height: 100%;
  align-items: center;
  flex: 1 0px;
  justify-content: ${({ align = 'start' }) =>
    align === 'center' ? 'center' : `flex-${align}`};
`

const NavbarLogoWrapper = styled(NavbarLinkWrapper)`
  display: flex;
  height: ${rem(48)};
  width: ${rem(48)};
  align-items: center;
  justify-content: center;
  padding: 0;
`

const NavbarLogo = ({ variant }: { variant?: 'colored' | 'black' }) => (
  <NavbarLogoWrapper>
    {variant === 'black' ? (
      <LogoBlack width="20px" height="18.7px" style={{ margin: 'auto' }} />
    ) : (
      <Logo width="27px" height="25px" style={{ margin: 'auto' }} />
    )}
  </NavbarLogoWrapper>
)

export const navbarTitleCss = css`
  ${truncateStyles};

  color: ${({ theme }) => theme.colors.text.dark.base};
  min-width: 25px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`

const NavbarTitle = styled(Text).attrs({
  'data-testid': 'navbar-title',
})`
  ${navbarTitleCss};
`

const Navbar = styled.div<{
  fixed?: boolean
  isSidebarLeftOpen?: boolean
  /** There's only a single children element */
  singleContent?: boolean
}>(
  ({ theme, isSidebarLeftOpen, fixed, singleContent }) => css`
    position: relative;
    display: flex;
    /* Adding 1px to make room for border-bottom */
    height: ${theme.header.height + 1}px;
    border-bottom: 1px solid ${theme.colors.border[2]};
    width: 100%;
    align-items: center;
    justify-content: ${singleContent ? 'flex-end' : 'space-between'};
    background: ${theme.colors.background.light.base};

    transition-timing-function: ease-in-out;
    transition-duration: 0.3s;
    transition-property: transform, opacity;

    /* For mobile, when the sidebar is opened we also need to move the banner (if there is any) */
    & + aside[role='alert'] {
      transition-timing-function: ease-in-out;
      transition-duration: 0.3s;
      transition-property: transform, opacity;

      transform: ${isSidebarLeftOpen
        ? `translateX(${theme.sidebar.leftWidth}px)`
        : undefined};
    }

    ${breakpoint('sm')`
      transform: none;
    `};

    ${fixed &&
    css`
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 100;
    `};
  `
)

const NavbarDivider = styled(GradientSeparator)`
  display: none;
`

export default Object.assign(Navbar, {
  Button: NavbarButton,
  Divider: NavbarDivider,
  Section: NavbarSection,
  Logo: NavbarLogo,
  Title: NavbarTitle,
  Filter: NavbarFilter,
})
